import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CalendarBlank, MagnifyingGlassMinus, MagnifyingGlassPlus, ArrowsClockwise, Lightning, Users, Stack } from '@phosphor-icons/react';

// ─── Helpers ─────────────────────────────────────────────────────────
const D = (s) => (s ? new Date(s) : null);
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const dayDiff = (a, b) => Math.round((startOfDay(b) - startOfDay(a)) / 86400000);
const fmtMonthDay = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const fmtMonth = (d) => d.toLocaleDateString('en-US', { month: 'short' });

// Per-status colors for the bars.
const STATUS_COLOR = {
  todo: { bg: '#e4e4e7', border: '#a1a1aa', text: '#3f3f46' },
  in_progress: { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' },
  review: { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
  done: { bg: '#d1fae5', border: '#10b981', text: '#065f46' },
};
const colorFor = (s) => STATUS_COLOR[s] || { bg: '#e4e4e7', border: '#a1a1aa', text: '#3f3f46' };

// Determine effective start/end for a task's bar in the timeline.
// Falls back to created_at if start_date is unset; falls back to start+3 days
// or "today" if due_date is unset, so every task gets at least a small bar.
function taskRange(task) {
  const sd = D(task.start_date) || D(task.created_at) || new Date();
  let ed = D(task.due_date) || addDays(sd, 3);
  if (ed < sd) ed = sd; // Sanity clamp
  return { start: startOfDay(sd), end: startOfDay(ed) };
}

// Group tasks by sprint or assignee.
function groupTasks(tasks, sprints, members, mode) {
  if (mode === 'assignee') {
    const map = new Map();
    map.set('__unassigned__', { id: '__unassigned__', label: 'Unassigned', tasks: [] });
    for (const m of members) map.set(m.user_id, { id: m.user_id, label: m.name, tasks: [], avatar: m.avatar });
    for (const t of tasks) {
      const k = t.assignee_id || '__unassigned__';
      if (!map.has(k)) map.set(k, { id: k, label: 'Unknown', tasks: [] });
      map.get(k).tasks.push(t);
    }
    return [...map.values()].filter(g => g.tasks.length > 0);
  }
  // Default: by sprint (with Backlog group at top).
  const map = new Map();
  map.set('__backlog__', { id: '__backlog__', label: 'Backlog', tasks: [] });
  for (const s of (sprints || [])) {
    map.set(s.sprint_id, { id: s.sprint_id, label: s.name, tasks: [], status: s.status, start_date: s.start_date, end_date: s.end_date });
  }
  for (const t of tasks) {
    const k = t.sprint_id || '__backlog__';
    if (!map.has(k)) map.set(k, { id: k, label: 'Sprint', tasks: [] });
    map.get(k).tasks.push(t);
  }
  return [...map.values()].filter(g => g.tasks.length > 0);
}

// ─── Component ───────────────────────────────────────────────────────
export default function TimelineView({ tasks = [], sprints = [], members = [], links = [], onTaskClick, onTaskDateChange }) {
  const [groupBy, setGroupBy] = useState('sprint'); // 'sprint' | 'assignee'
  const [zoom, setZoom] = useState('week'); // 'day' | 'week' | 'month'

  // Drag state for resizable / movable bars. While a drag is in progress
  // we keep the user-perceived offsets in `dragPreview` so the bar tracks
  // the cursor in real time without re-rendering parent state on every
  // pixel move. On mouseup we commit to the backend via onTaskDateChange.
  const [dragPreview, setDragPreview] = useState(null);
  // { taskId, mode, startClientX, origStart: Date, origEnd: Date, deltaDays: 0 }

  // Survives across the mouseup → click sequence so we can distinguish
  // a true drag from a plain click. `dragPreview` is null by the time
  // `onClick` fires (it was cleared on mouseup), so we can't use it as
  // a guard there. This ref is reset on every mousedown.
  const wasDraggedRef = useRef(false);

  // Global drag handlers. We attach to window so the user can drag well
  // outside the bar without losing the gesture (mouseup anywhere ends).
  useEffect(() => {
    if (!dragPreview) return;
    const onMove = (e) => {
      // Compute days delta using the current pxPerDayRef (kept fresh below).
      const px = pxPerDayRef.current || 1;
      const delta = Math.round((e.clientX - dragPreview.startClientX) / px);
      // ANY pointer movement larger than the click slop (3 px) marks this
      // as a real drag. Required because day-zoom delta=0 for small moves
      // would otherwise let a click through after a mini-drag.
      if (Math.abs(e.clientX - dragPreview.startClientX) > 3) {
        wasDraggedRef.current = true;
      }
      if (delta !== dragPreview.deltaDays) {
        setDragPreview(d => d ? { ...d, deltaDays: delta } : null);
      }
    };
    const onUp = async () => {
      // Snapshot the value before clearing — otherwise onTaskDateChange runs
      // after state reset and we lose the delta.
      const dp = dragPreview;
      setDragPreview(null);
      // Reset the "was dragged" flag on the NEXT tick so the click handler
      // that follows immediately can read it as true (and bail out).
      setTimeout(() => { wasDraggedRef.current = false; }, 0);
      if (!dp || dp.deltaDays === 0 || !onTaskDateChange) return;
      let newStart = dp.origStart, newEnd = dp.origEnd;
      if (dp.mode === 'move') {
        newStart = addDays(dp.origStart, dp.deltaDays);
        newEnd = addDays(dp.origEnd, dp.deltaDays);
      } else if (dp.mode === 'resize-left') {
        const ns = addDays(dp.origStart, dp.deltaDays);
        if (ns <= dp.origEnd) newStart = ns;
      } else if (dp.mode === 'resize-right') {
        const ne = addDays(dp.origEnd, dp.deltaDays);
        if (ne >= dp.origStart) newEnd = ne;
      }
      const iso = (d) => startOfDay(d).toISOString().slice(0, 10);
      try {
        await onTaskDateChange(dp.taskId, { start_date: iso(newStart), due_date: iso(newEnd) });
      } catch (e) { /* parent surfaces error via toast */ }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragPreview, onTaskDateChange]);

  // We need a ref to the current px-per-day so the global mousemove
  // handler captures it without re-binding on every zoom change.
  const pxPerDayRef = useRef(20);

  // Compute time window: from the earliest task start to the latest end + 7 days padding.
  const { startDate, endDate } = useMemo(() => {
    if (tasks.length === 0) {
      const t = startOfDay(new Date());
      return { startDate: addDays(t, -7), endDate: addDays(t, 21) };
    }
    let min = new Date(8.64e15), max = new Date(-8.64e15);
    for (const t of tasks) {
      const { start, end } = taskRange(t);
      if (start < min) min = start;
      if (end > max) max = end;
    }
    // Include sprints too.
    for (const s of sprints) {
      if (s.start_date && D(s.start_date) < min) min = startOfDay(D(s.start_date));
      if (s.end_date && D(s.end_date) > max) max = startOfDay(D(s.end_date));
    }
    return { startDate: addDays(min, -3), endDate: addDays(max, 7) };
  }, [tasks, sprints]);

  // Pixels per day depending on zoom.
  const pxPerDay = zoom === 'day' ? 48 : zoom === 'week' ? 18 : zoom === 'month' ? 6 : 18;
  // Keep the ref in sync with the latest computed pxPerDay so the global
  // mousemove handler (bound once) reads the live value even after zoom
  // changes mid-drag.
  pxPerDayRef.current = pxPerDay;
  const totalDays = Math.max(1, dayDiff(startDate, endDate) + 1);
  const chartW = totalDays * pxPerDay;
  const rowH = 32;
  const headerH = 48;
  const sidebarW = 240;

  // Build groups + flatten rows in display order.
  const groups = useMemo(() => groupTasks(tasks, sprints, members, groupBy), [tasks, sprints, members, groupBy]);
  const rows = useMemo(() => {
    const r = [];
    for (const g of groups) {
      r.push({ kind: 'group', group: g });
      for (const t of g.tasks) r.push({ kind: 'task', task: t, group: g });
    }
    return r;
  }, [groups]);

  // Build a task → row-index map for drawing dependency arrows.
  const taskRowIndex = useMemo(() => {
    const m = {};
    rows.forEach((r, i) => { if (r.kind === 'task') m[r.task.task_id] = i; });
    return m;
  }, [rows]);

  const dayX = (d) => dayDiff(startDate, d) * pxPerDay;
  const today = startOfDay(new Date());

  // Header tick generation — month markers always; week ticks for week zoom; day ticks for day zoom.
  const headerTicks = useMemo(() => {
    const ticks = [];
    const cur = new Date(startDate);
    while (cur <= endDate) {
      ticks.push(new Date(cur));
      if (zoom === 'day') cur.setDate(cur.getDate() + 1);
      else if (zoom === 'week') cur.setDate(cur.getDate() + 7);
      else cur.setDate(cur.getDate() + 7);
    }
    return ticks;
  }, [startDate, endDate, zoom]);

  // Month-band (top row of the header) — segments of consecutive days within the same month.
  const monthBands = useMemo(() => {
    const bands = [];
    let cur = new Date(startDate);
    while (cur <= endDate) {
      const monthStart = new Date(cur.getFullYear(), cur.getMonth(), 1);
      const monthEnd = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
      const segStart = cur > monthStart ? cur : monthStart;
      const segEnd = endDate < monthEnd ? endDate : monthEnd;
      bands.push({
        label: monthStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        x: dayX(segStart),
        w: (dayDiff(segStart, segEnd) + 1) * pxPerDay,
      });
      cur = addDays(monthEnd, 1);
    }
    return bands;
  }, [startDate, endDate, pxPerDay]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build dependency arrows: only draw "blocks" (forward) so we don't get duplicates.
  const arrows = useMemo(() => {
    const arr = [];
    for (const l of links || []) {
      if (l.link_type !== 'blocks') continue;
      const fromIdx = taskRowIndex[l.source_task_id];
      const toIdx = taskRowIndex[l.target_task_id];
      if (fromIdx == null || toIdx == null) continue;
      const fromTask = rows[fromIdx].task;
      const toTask = rows[toIdx].task;
      const a = taskRange(fromTask);
      const b = taskRange(toTask);
      const x1 = dayX(a.end) + pxPerDay; // right edge of source bar
      const y1 = headerH + fromIdx * rowH + rowH / 2;
      const x2 = dayX(b.start);          // left edge of target bar
      const y2 = headerH + toIdx * rowH + rowH / 2;
      arr.push({ x1, y1, x2, y2, key: l.link_id });
    }
    return arr;
  }, [links, taskRowIndex, rows]); // eslint-disable-line react-hooks/exhaustive-deps

  if (tasks.length === 0) {
    return (
      <div className="flex-1 h-full flex items-center justify-center bg-zinc-50/30" data-testid="timeline-empty">
        <div className="text-center max-w-md p-8">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-blue-100 to-violet-100 flex items-center justify-center mb-4">
            <CalendarBlank size={28} className="text-blue-600" weight="duotone" />
          </div>
          <h3 className="font-['Outfit'] text-[20px] font-bold text-zinc-800 mb-1">Timeline view</h3>
          <p className="text-[13px] text-zinc-500">Once you create tasks with start &amp; due dates, you'll see them here as a Gantt-style timeline with dependency arrows from your linked issues.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 h-full overflow-hidden flex flex-col bg-white" data-testid="timeline-view">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-5 py-2.5 border-b border-zinc-200 flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-bold">Group by</span>
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger className="h-7 w-32 text-[11px]" data-testid="timeline-group-by">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sprint"><Stack size={12} className="inline mr-1.5" /> Sprint</SelectItem>
              <SelectItem value="assignee"><Users size={12} className="inline mr-1.5" /> Assignee</SelectItem>
            </SelectContent>
          </Select>
          <span className="ml-3 text-[11px] uppercase tracking-wider text-zinc-500 font-bold">Zoom</span>
          <Button size="sm" variant={zoom === 'day' ? 'default' : 'outline'} onClick={() => setZoom('day')} className="h-7 text-[11px]" data-testid="timeline-zoom-day">Day</Button>
          <Button size="sm" variant={zoom === 'week' ? 'default' : 'outline'} onClick={() => setZoom('week')} className="h-7 text-[11px]" data-testid="timeline-zoom-week">Week</Button>
          <Button size="sm" variant={zoom === 'month' ? 'default' : 'outline'} onClick={() => setZoom('month')} className="h-7 text-[11px]" data-testid="timeline-zoom-month">Month</Button>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-zinc-500">
          <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-zinc-200 border border-zinc-400" /> To Do</span>
          <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-blue-200 border border-blue-500" /> In Progress</span>
          <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-amber-200 border border-amber-500" /> Review</span>
          <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-emerald-200 border border-emerald-500" /> Done</span>
        </div>
      </div>

      {/* Chart area */}
      <div className="flex-1 overflow-auto relative" data-testid="timeline-chart-scroll">
        <div className="relative inline-flex" style={{ minWidth: '100%' }}>
          {/* Sticky left sidebar with task labels */}
          <div className="sticky left-0 z-20 flex-shrink-0 bg-white border-r border-zinc-200 shadow-[2px_0_4px_rgba(0,0,0,0.04)]" style={{ width: sidebarW }}>
            <div className="border-b border-zinc-200 bg-zinc-50/60" style={{ height: headerH }}>
              <div className="px-3 h-full flex items-center text-[11px] uppercase tracking-wider font-bold text-zinc-500">Task</div>
            </div>
            {rows.map((r, i) => (
              r.kind === 'group' ? (
                <div key={`g-${r.group.id}`} style={{ height: rowH }}
                  className="px-3 flex items-center bg-zinc-50 border-b border-zinc-100">
                  <span className="text-[11px] font-bold text-zinc-700 truncate uppercase tracking-wide">{r.group.label}</span>
                  <span className="text-[10px] text-zinc-400 ml-auto tabular-nums">{r.group.tasks.length}</span>
                </div>
              ) : (
                <button key={r.task.task_id} onClick={() => onTaskClick && onTaskClick(r.task)}
                  data-testid={`timeline-row-${r.task.task_id}`}
                  className="w-full text-left px-3 flex items-center gap-2 border-b border-zinc-50 hover:bg-zinc-50 transition-colors"
                  style={{ height: rowH }}>
                  <span className="font-mono text-[10px] text-zinc-400 flex-shrink-0">{r.task.task_key}</span>
                  <span className="text-[12px] text-zinc-700 truncate">{r.task.title}</span>
                </button>
              )
            ))}
          </div>

          {/* Right side: header + bars + grid + arrows + today */}
          <div className="relative flex-shrink-0" style={{ width: chartW, height: headerH + rows.length * rowH }}>
            {/* Header — month bands + tick row */}
            <div className="absolute top-0 left-0 right-0 border-b border-zinc-200 bg-zinc-50/60" style={{ height: headerH }}>
              <div className="relative" style={{ height: 22 }}>
                {monthBands.map((b, i) => (
                  <div key={i} className="absolute top-0 h-full flex items-center px-2 border-r border-zinc-200 bg-white"
                    style={{ left: b.x, width: b.w }}>
                    <span className="text-[10px] font-bold text-zinc-700 uppercase tracking-wider">{b.label}</span>
                  </div>
                ))}
              </div>
              <div className="relative" style={{ height: headerH - 22 }}>
                {headerTicks.map((t, i) => (
                  <div key={i} className="absolute top-0 h-full px-1 flex items-center border-r border-zinc-100"
                    style={{ left: dayX(t), width: zoom === 'day' ? pxPerDay : pxPerDay * 7 }}>
                    <span className="text-[9px] font-medium text-zinc-500 tabular-nums">{fmtMonthDay(t)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Vertical grid lines + today line + sprint bands */}
            <svg className="absolute inset-0 pointer-events-none" style={{ top: headerH, width: chartW, height: rows.length * rowH }}>
              {/* Sprint date-range bands (drawn behind bars) */}
              {groupBy === 'sprint' && groups.map((g, gi) => {
                if (!g.start_date || !g.end_date) return null;
                const x = dayX(startOfDay(D(g.start_date)));
                const w = Math.max(2, (dayDiff(startOfDay(D(g.start_date)), startOfDay(D(g.end_date))) + 1) * pxPerDay);
                return (
                  <rect key={`sp-${g.id}`} x={x} y={0} width={w} height={rows.length * rowH}
                    fill={g.status === 'active' ? '#3b82f6' : '#a1a1aa'} fillOpacity="0.04" />
                );
              })}
              {/* Vertical week tick lines */}
              {headerTicks.map((t, i) => (
                <line key={i} x1={dayX(t)} x2={dayX(t)} y1={0} y2={rows.length * rowH} stroke="#f4f4f5" strokeWidth="1" />
              ))}
              {/* Today line */}
              {today >= startDate && today <= endDate && (
                <g>
                  <line x1={dayX(today) + pxPerDay / 2} x2={dayX(today) + pxPerDay / 2} y1={0} y2={rows.length * rowH}
                    stroke="#ef4444" strokeWidth="1.5" strokeDasharray="3 2" />
                  <circle cx={dayX(today) + pxPerDay / 2} cy={4} r="3" fill="#ef4444" />
                </g>
              )}
              {/* Dependency arrows */}
              {arrows.map(a => {
                const midX = (a.x1 + a.x2) / 2;
                const path = `M ${a.x1} ${a.y1 - headerH} L ${midX} ${a.y1 - headerH} L ${midX} ${a.y2 - headerH} L ${a.x2 - 4} ${a.y2 - headerH}`;
                return (
                  <g key={a.key}>
                    <path d={path} stroke="#a78bfa" strokeWidth="1.5" fill="none" strokeDasharray="2 2" />
                    <polygon points={`${a.x2 - 4},${a.y2 - headerH - 3} ${a.x2 + 1},${a.y2 - headerH} ${a.x2 - 4},${a.y2 - headerH + 3}`} fill="#a78bfa" />
                  </g>
                );
              })}
            </svg>

            {/* Task rows + bars */}
            <div className="absolute left-0 right-0" style={{ top: headerH }}>
              {rows.map((r, i) => {
                if (r.kind === 'group') {
                  return <div key={`gr-${r.group.id}`} className="bg-zinc-50/50 border-b border-zinc-100" style={{ height: rowH }} />;
                }
                // Effective range — if this row is currently being dragged,
                // shift / extend the bar by the in-progress deltaDays so the
                // user gets immediate visual feedback before commit.
                let { start, end } = taskRange(r.task);
                if (dragPreview && dragPreview.taskId === r.task.task_id) {
                  const d = dragPreview.deltaDays;
                  if (dragPreview.mode === 'move') {
                    start = addDays(start, d);
                    end = addDays(end, d);
                  } else if (dragPreview.mode === 'resize-left') {
                    const ns = addDays(start, d);
                    if (ns <= end) start = ns;
                  } else if (dragPreview.mode === 'resize-right') {
                    const ne = addDays(end, d);
                    if (ne >= start) end = ne;
                  }
                }
                const x = dayX(start);
                const w = Math.max(8, (dayDiff(start, end) + 1) * pxPerDay);
                const c = colorFor(r.task.status);
                const isOverdue = r.task.due_date && D(r.task.due_date) < today && r.task.status !== 'done';
                const canDrag = !!onTaskDateChange;
                const beginDrag = (mode) => (e) => {
                  if (!canDrag) return;
                  e.preventDefault(); e.stopPropagation();
                  // Reset the click-vs-drag flag at the start of every
                  // gesture — only mousemove past the slop threshold
                  // will flip it back to true.
                  wasDraggedRef.current = false;
                  setDragPreview({
                    taskId: r.task.task_id, mode,
                    startClientX: e.clientX,
                    origStart: taskRange(r.task).start,
                    origEnd: taskRange(r.task).end,
                    deltaDays: 0,
                  });
                };
                return (
                  <div key={r.task.task_id} className="border-b border-zinc-50 hover:bg-zinc-50/40 transition-colors relative" style={{ height: rowH }}>
                    <div
                      data-testid={`timeline-bar-${r.task.task_id}`}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        // If the user actually dragged (movement >3px), do NOT
                        // open the task detail panel — they meant to reschedule
                        // not to navigate. wasDraggedRef survives the
                        // mouseup→click sequence (cleared on next tick).
                        if (wasDraggedRef.current) {
                          e.preventDefault(); e.stopPropagation();
                          return;
                        }
                        onTaskClick && onTaskClick(r.task);
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter') onTaskClick && onTaskClick(r.task); }}
                      onMouseDown={canDrag ? beginDrag('move') : undefined}
                      className={`absolute rounded-md border-2 flex items-center hover:shadow-md transition-shadow group select-none ${canDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`}
                      style={{
                        top: 5, height: rowH - 10, left: x, width: w,
                        backgroundColor: c.bg, borderColor: isOverdue ? '#ef4444' : c.border,
                        paddingLeft: canDrag ? 8 : 6, paddingRight: canDrag ? 8 : 6,
                      }}
                      title={`${r.task.task_key}: ${r.task.title} — ${fmtMonthDay(start)} → ${fmtMonthDay(end)}${canDrag ? ' (drag bar to move • drag edges to resize)' : ''}`}
                    >
                      {/* Left resize handle — drag changes start_date only */}
                      {canDrag && w >= 22 && (
                        <div
                          onMouseDown={beginDrag('resize-left')}
                          className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/40 rounded-l-md"
                          data-testid={`timeline-resize-left-${r.task.task_id}`}
                          title="Drag to change start date"
                        />
                      )}
                      {w >= 60 && (
                        <span className="text-[10px] font-semibold truncate pointer-events-none" style={{ color: c.text }}>
                          {r.task.task_key}
                        </span>
                      )}
                      {/* Right resize handle — drag changes due_date only */}
                      {canDrag && w >= 22 && (
                        <div
                          onMouseDown={beginDrag('resize-right')}
                          className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/40 rounded-r-md"
                          data-testid={`timeline-resize-right-${r.task.task_id}`}
                          title="Drag to change due date"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
